/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import { ApplicationContext } from '../App'
import { ClaimsMockPanel } from '../components/dialogs/ClaimsMockPanel'
import ConfirmPaymentDialog from '../components/dialogs/ConfirmPaymentDialog'
import ErrorDialog, { formatError } from '../components/dialogs/ErrorDialog'
import { ValidationSummaryDialog } from '../components/dialogs/ValidationSummaryDialog'
import { getPaymentFeeSchedule, submitRewardPayment } from '../services/blockchainService'
import { postClaim, validateClaim } from '../services/claimsService'
import { EdiToJson, getConfig } from '../services/miscService'

export const SubmitRaw: React.FC = () => {
    const navigate = useNavigate()
    const { identities } = useContext(ApplicationContext)!
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone()
    const [importType, setImportType] = useState<'file' | 'clipboard' | 'mock'>('file')
    const [errorDescription, setErrorDescription] = useState('')
    const [ediContent, setEdiContent] = useState('')
    const [mockContent, setMockContent] = useState('')
    const [validationPassed, setValidationPassed] = useState(false)
    const [type, setType] = useState('')
    const [recipient, setRecipient] = useState('')
    const [mockDataDialogOpen, setMockDataDialogOpen] = useState(false)
    const [validationSummaryData, setValidationSummaryData] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
    const [paymentDialogData, setPaymentDialogData] = useState<{
        infrastructureFee: number
        payerFee: number
        rhizaFee: number
        total: number
    }>()
    const [ediData, setEdiData] = useState<FormData>()
    const [errorData, setErrorData] = useState<any>()
    const [errorOpen, setErrorOpen] = useState(false)
    const [blockchainPaymentEnabled, setBlockchainPaymentEnabled] = useState(true)

    const handleProcessEDI = (submit: boolean, data: FormData, forceSubmit = false) =>
        (submit ? postClaim(data, forceSubmit) : validateClaim(data))
            .then((response) => {
                setLoading(false)
                if (response && response.data) {
                    const result = response.data
                    if (submit) {
                        navigate(
                            `/claims/${
                                result.publicData.claimNumber ??
                                result.publicData.payments[0].patientControlNumber
                            }/summary`,
                        )
                    } else {
                        setType(result.type)
                        setRecipient(
                            identities.find(
                                (entry) => entry.interchangeId === result.interchangeRecipient,
                            )?.displayName || '',
                        )
                        setValidationPassed(true)
                        if (result.type === '837' || result.type === '837R') {
                            const summary = {
                                messageType: result.type,
                                source: identities.find(
                                    (entry) => entry.interchangeId === result.interchangeSender,
                                )!.displayName,
                                destination: identities.find(
                                    (entry) => entry.interchangeId === result.interchangeRecipient,
                                )!.displayName,
                                claimNumber: result.publicData.claimNumber,
                            }
                            setValidationSummaryData(summary)
                        }
                    }
                } else {
                    try {
                        const detail = response?.details
                        if (detail.type === '837' || detail.type === '837R') {
                            const summary = {
                                messageType: detail?.type,
                                source: detail?.interchangeSender
                                    ? identities.find(
                                          (entry) =>
                                              entry.interchangeId === detail?.interchangeSender,
                                      )!.displayName
                                    : 'INVALID',
                                destination: detail?.interchangeRecipient
                                    ? identities.find(
                                          (entry) =>
                                              entry.interchangeId === detail?.interchangeRecipient,
                                      )!.displayName
                                    : 'INVALID',
                                claimNumber: detail?.claimNumber,
                            }
                            setValidationSummaryData(summary)
                        } else if (response?.error) {
                            setErrorDescription(response.error)
                        }
                    } catch (err) {
                        setErrorDescription('Failed to parse X12')
                    }
                }
            })
            .catch((error) => {
                setLoading(false)
                setErrorData(formatError(error))
                setErrorOpen(true)
                const detail = error?.response?.data.details
                if ((detail && detail?.type === '837') || detail?.type === '837R') {
                    const summary = {
                        messageType: detail?.type,
                        source: detail?.interchangeSender
                            ? identities.find(
                                  (entry) => entry.interchangeId === detail?.interchangeSender,
                              )!.displayName
                            : 'INVALID',
                        destination: detail?.interchangeRecipient
                            ? identities.find(
                                  (entry) => entry.interchangeId === detail?.interchangeRecipient,
                              )!.displayName
                            : 'INVALID',
                        claimNumber: detail?.claimNumber,
                    }
                    setValidationSummaryData(summary)
                } else if (error?.response?.data.error) {
                    setErrorDescription(error?.response?.data.error)
                } else {
                    setErrorDescription('Failed to parse X12')
                }
            })

    const processEDI = async (submit: boolean) => {
        setErrorDescription('')
        setLoading(true)
        const data = new FormData()
        setEdiData(data)
        switch (importType) {
            case 'file':
                data.append('files', importType === 'file' ? acceptedFiles[0] : ediContent)
                break
            case 'clipboard':
                data.append('files', new Blob([ediContent]))
                break
            case 'mock':
                data.append('files', new Blob([mockContent]))
                break
        }
        if (submit) {
            if (!data || !data?.get('files')) {
                setErrorData('Failed to digest EDI data.')
                setErrorOpen(true)
                return
            }
            // TODO: state-ify parsed edi
            const {
                data: {
                    headers: { type },
                },
            } = await EdiToJson(data)
            if (blockchainPaymentEnabled && type !== '835') {
                // TODO: handle for if payments go through but edi does not
                setPaymentDialogOpen(true)
            } else {
                handleProcessEDI(true, data, true)
            }
        } else {
            handleProcessEDI(false, data)
        }
    }

    const handleClose = (data: any) => {
        setMockDataDialogOpen(false)
        if (data && data.toString().length > 0) {
            setMockContent(data.toString())
        }
        setLoading(false)
    }

    const handleSubmitPayment = async () => {
        setPaymentDialogOpen(false)
        if (!ediData || !ediData?.get('files')) {
            setErrorData('Failed to digest EDI data.')
            setErrorOpen(true)
        } else {
            const {
                data: {
                    claim: { claimNumber },
                },
            } = await EdiToJson(ediData)
            ediData.append('claimNumber', claimNumber)
            // TODO: handle for if payments go through but edi does not
            submitRewardPayment(claimNumber).then((response) => {
                ediData.append('paymentId', response.data.paymentId)
                handleProcessEDI(true, ediData)
            })
        }
    }

    useEffect(() => {
        if (acceptedFiles.length === 0) {
            setErrorDescription('')
            setValidationPassed(false)
        }
    }, [acceptedFiles])

    useEffect(() => {
        const fetchProcessingFees = async () => {
            await getConfig()
                .then(async (config) => {
                    setBlockchainPaymentEnabled(config.data.blockchainPaymentsEnabled)
                    if (config.data.blockchainPaymentsEnabled) {
                        await getPaymentFeeSchedule()
                            .then((payouts) => {
                                setPaymentDialogData({
                                    infrastructureFee: payouts.adminPayout,
                                    payerFee: payouts.payerPayout,
                                    rhizaFee: payouts.otherPayout,
                                    total: payouts.total,
                                })
                            })
                            .catch((err) => {
                                console.log(err)
                            })
                    }
                })
                .catch((err) => {
                    console.log(err)
                })
        }
        fetchProcessingFees()
    }, [])

    const fileTab = (
        <Box
            {...getRootProps()}
            style={{
                marginTop: '30px',
                borderRadius: '10px',
                height: '192px',
                border: 'dashed 1px #008AEE',
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
            }}
        >
            <input {...getInputProps()} />
            {acceptedFiles.length === 0 ? (
                <Typography style={{ lineHeight: '200px' }}>
                    Click here or drag file to this area
                </Typography>
            ) : (
                <Typography
                    style={{ lineHeight: '200px' }}
                >{`File ${acceptedFiles[0].name} selected`}</Typography>
            )}
        </Box>
    )
    const ediContentTab = (
        <Box>
            <TextField
                value={ediContent}
                onChange={(event) => {
                    setValidationPassed(false)
                    setEdiContent(event.target.value)
                }}
                style={{ marginTop: '30px', backgroundColor: 'white' }}
                multiline
                fullWidth
                rows={7}
            />
        </Box>
    )
    const mockClaimsTab = (
        <Box>
            <TextField
                value={mockContent}
                onChange={(event) => {
                    setValidationPassed(false)
                    setMockContent(event.target.value)
                }}
                style={{ marginTop: '30px', backgroundColor: 'white' }}
                multiline
                fullWidth
                rows={7}
            />
        </Box>
    )

    return (
        <Grid container direction="column" style={{ padding: '40px' }}>
            <Grid item>
                <Typography variant="h3">Submit a claim</Typography>
            </Grid>
            <Grid item>
                <Typography style={{ marginTop: '10px' }}>
                    Use the form below to submit new claims to the Rhiza Health system.
                </Typography>
            </Grid>
            <Grid item>
                <Box
                    style={{
                        borderRadius: '10px',
                        marginTop: '30px',
                        padding: '30px',
                        backgroundColor: '#F2F6F9',
                    }}
                >
                    <Typography variant="h6">Upload Claim Data</Typography>
                    <Typography style={{ marginTop: '10px' }}>
                        Claims should be added using X12 data format.
                    </Typography>
                    <ToggleButtonGroup
                        style={{ marginTop: '30px' }}
                        size="small"
                        color="primary"
                        value={importType}
                        exclusive
                        onChange={(_event, value) => {
                            setValidationPassed(false)
                            setImportType(value ?? importType)
                        }}
                    >
                        <ToggleButton value="file">Upload data file</ToggleButton>
                        <ToggleButton value="clipboard">Paste from clipboard</ToggleButton>
                        <ToggleButton value="mock" onClick={() => setMockDataDialogOpen(true)}>
                            Create test data
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {importType === 'file'
                        ? fileTab
                        : importType === 'mock'
                        ? mockClaimsTab
                        : ediContentTab}
                    <Box
                        style={{
                            marginTop: '30px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box>
                            {errorDescription.length > 0 && (
                                <Typography style={{ color: 'red' }}>{errorDescription}</Typography>
                            )}
                            {validationPassed && (
                                <Typography style={{ color: 'green' }}>
                                    Successfully validated {type} targeted to {recipient}.
                                </Typography>
                            )}
                        </Box>
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                size="large"
                                disableElevation={true}
                                variant="contained"
                                disabled={
                                    (importType === 'file' && acceptedFiles.length === 0) ||
                                    (importType === 'clipboard' && ediContent.length === 0) ||
                                    (importType === 'mock' && mockContent.length === 0) ||
                                    validationPassed
                                }
                                onClick={() => processEDI(false)}
                                style={{ minWidth: '120px' }}
                            >
                                Validate
                            </Button>
                            <Button
                                size="large"
                                disableElevation={true}
                                variant="contained"
                                onClick={() => processEDI(true)}
                                style={{ marginLeft: '10px', minWidth: '120px' }}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                    <Backdrop open={loading}>
                        {' '}
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </Box>
            </Grid>
            <ClaimsMockPanel open={mockDataDialogOpen} close={handleClose} />
            <ValidationSummaryDialog
                entry={validationSummaryData}
                open={validationSummaryData !== null}
                close={() => setValidationSummaryData(null)}
            />
            <ConfirmPaymentDialog
                open={paymentDialogOpen}
                onSubmit={handleSubmitPayment}
                onClose={() => {
                    setLoading(false)
                    setPaymentDialogOpen(false)
                }}
                data={paymentDialogData}
            />
            <ErrorDialog
                open={errorOpen}
                data={errorData}
                onClose={() => {
                    setLoading(false)
                    setErrorOpen(false)
                }}
            />
        </Grid>
    )
}
